import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectContractAcceptanceApi from "../../../../../infrastructure/api/projectContractAcceptance";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("fa-IR");
};

const ContractorProjectContractAcceptancePage = () => {
  const [loading, setLoading] = useState(false);
  const [acceptingId, setAcceptingId] = useState(0);
  const [contracts, setContracts] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    item: null,
  });

  const loadContracts = async () => {
    setLoading(true);
    setError("");
    const res = await ProjectContractAcceptanceApi.getPendingContracts();
    if (!res.ok) {
      setError(res.message || "خطا در دریافت قراردادها");
      setContracts([]);
    } else {
      setContracts(Array.isArray(res.data) ? res.data : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const openConfirm = (item) => {
    setConfirmDialog({ open: true, item });
    setError("");
    setSuccess("");
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, item: null });
  };

  const handleAccept = async () => {
    const item = confirmDialog.item;
    if (!item?.projectContractId) return;

    setAcceptingId(item.projectContractId);
    setError("");
    setSuccess("");
    const res = await ProjectContractAcceptanceApi.acceptContract(
      item.projectContractId
    );
    if (!res.ok) {
      setError(res.message || "قبول قرارداد با خطا مواجه شد.");
    } else {
      setSuccess(res.message || "قرارداد با موفقیت پذیرفته شد.");
      closeConfirm();
      await loadContracts();
    }
    setAcceptingId(0);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent
          items={[
            { title: "SLN", path: "/SLN" },
            { title: "پیمانکار", path: "/SLN/Contractor" },
            { title: "قراردادهای آماده پذیرش" },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="قراردادهای آماده پذیرش"
            subheader="قراردادهای صادرشده از پیشنهاد قیمت پذیرفته‌شده که منتظر تأیید و قبول شما هستند."
            action={
              <IconButton onClick={loadContracts} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            {loading ? (
              <Typography variant="body2" color="text.secondary">
                در حال بارگذاری...
              </Typography>
            ) : contracts.length === 0 ? (
              <Alert severity="info">
                در حال حاضر قراردادی برای پذیرش در اختیار شما نیست.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>پروژه</TableCell>
                    <TableCell>مبلغ (ریال)</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>فایل قرارداد</TableCell>
                    <TableCell align="left">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contracts.map((item) => (
                    <TableRow key={item.projectContractId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.requestTitle || `درخواست ${item.requestId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          شماره: {item.requestReferenceId || item.requestId}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPrice(item.price)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            item.projectContractStatusTitle ||
                            item.projectContractStatus
                          }
                          color="warning"
                        />
                      </TableCell>
                      <TableCell>
                        {item.contractFileUrl ? (
                          <Link
                            href={item.contractFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            مشاهده فایل
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {item.isAlreadyAccepted ? (
                          <Chip
                            size="small"
                            color="success"
                            label="قبول شده"
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => openConfirm(item)}
                          >
                            قبول قرارداد
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تأیید قبول قرارداد</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              با تأیید این گام، مفاد قرارداد و مبلغ نهایی را می‌پذیرید. این
              اقدام در سامانه به‌صورت ثبت حقوقی ذخیره می‌شود.
            </Typography>
            {confirmDialog.item?.acceptancePreviewText ? (
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: "grey.50", whiteSpace: "pre-wrap" }}
              >
                <Typography variant="caption" color="text.secondary">
                  متن تعهد/قبولی:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {confirmDialog.item.acceptancePreviewText}
                </Typography>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={acceptingId > 0}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccept}
            disabled={acceptingId > 0}
          >
            {acceptingId > 0 ? "در حال ثبت..." : "قبول قرارداد"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ContractorProjectContractAcceptancePage;
